import React from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Card,
  CardHeader,
  CardBody,
  Badge,
  Icon,
  Grid,
  GridItem,
  Flex,
  Image,
  Alert,
  AlertIcon,
  Divider,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  useColorModeValue,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  List,
  ListItem,
  ListIcon,
  UnorderedList,
} from '@chakra-ui/react';
import {
  CalendarIcon,
  ExternalLinkIcon,
  ArrowBackIcon,
  ViewIcon,
  InfoIcon,
  CheckCircleIcon,
  TimeIcon,
  StarIcon,
} from '@chakra-ui/icons';
import { 
  getContest, 
  getContestStatusDisplay, 
  getContestStatusColor,
  getContestProblemName,
  getContestProblemShortName,
  Contest 
} from '../utils/contestUtils';

const ContestDetail: React.FC = () => {
  const { contestId } = useParams<{ contestId: string }>();
  const contest = contestId ? getContest(contestId) : null;
  
  // Color mode values
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textSecondary = useColorModeValue('gray.600', 'gray.400');
  const accentBg = useColorModeValue('blue.50', 'blue.900');

  if (!contest) {
    return (
      <Box bg={bgColor} minHeight="100vh">
        <Container maxW="7xl" py={8}>
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            Contest not found. Please check the URL and try again.
          </Alert>
          <Box mt={4}>
            <Button as={RouterLink} to="/contests" leftIcon={<ArrowBackIcon />}>
              Back to Contests
            </Button>
          </Box>
        </Container>
      </Box>
    );
  }

  const statusColor = getContestStatusColor(contest);
  const statusDisplay = getContestStatusDisplay(contest);

  const ProblemCard: React.FC<{ problemId: string; index: number }> = ({ problemId, index }) => {
    const problemName = getContestProblemName(problemId);
    const problemShortName = getContestProblemShortName(problemId);
    
    return (
      <Card 
        bg={cardBg} 
        borderColor={borderColor} 
        borderWidth="1px" 
        shadow="md"
        _hover={{ shadow: 'lg', transform: 'translateY(-2px)' }}
        transition="all 0.2s ease"
      >
        <CardHeader>
          <HStack justify="space-between">
            <VStack align="start" spacing={1}>
              <Badge colorScheme="blue" variant="outline">
                Problem {index + 1}
              </Badge>
              <Heading size="md">{problemShortName}</Heading>
            </VStack>
            <Icon as={ViewIcon} color="blue.500" boxSize={6} />
          </HStack>
        </CardHeader>
        
        <CardBody pt={0}>
          <VStack align="start" spacing={4}>
            <Text color={textSecondary} fontSize="sm">
              {problemName}
            </Text>
            
            <HStack spacing={3} width="100%">
              <Button
                as={RouterLink}
                to={`/problem/${problemId}`}
                colorScheme="blue"
                size="sm"
                leftIcon={<ViewIcon />}
                flex={1}
              >
                Solve Problem
              </Button>
              <Button
                as={RouterLink}
                to={`/contest/leaderboard/${problemId}`}
                size="sm"
                variant="outline"
                leftIcon={<StarIcon />}
              >
                Leaderboard
              </Button>
            </HStack>
          </VStack>
        </CardBody>
      </Card>
    );
  };

  // SPE4IC-specific content
  const getSPE4ICContent = () => {
    if (contest.id !== 'spe4ic25') return null;
    
    return (
      <VStack spacing={6} align="start">
        <Card bg={accentBg} borderColor={borderColor} borderWidth="1px">
          <CardHeader>
            <Heading size="md" color="blue.600">
              About SPE4IC'25
            </Heading>
          </CardHeader>
          <CardBody pt={0}>
            <VStack align="start" spacing={4}>
              <Text color={textSecondary}>
                The SPE4IC (Software Performance Engineering for Irregular Computation) track was a 
                special competition co-located with PPoPP'25, focusing on the challenging problem of 
                parallelizing irregular algorithms efficiently.
              </Text>
              
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} width="100%">
                <VStack align="start" spacing={3}>
                  <Text fontWeight="semibold" color="blue.600">
                    Competition Focus
                  </Text>
                  <UnorderedList spacing={1} color={textSecondary}>
                    <ListItem>Graph algorithm optimization</ListItem>
                    <ListItem>Parallel performance tuning</ListItem>
                    <ListItem>Memory efficiency improvements</ListItem>
                    <ListItem>Load balancing for irregular workloads</ListItem>
                  </UnorderedList>
                </VStack>
                
                <VStack align="start" spacing={3}>
                  <Text fontWeight="semibold" color="blue.600">
                    Technical Requirements
                  </Text>
                  <UnorderedList spacing={1} color={textSecondary}>
                    <ListItem>C++ with OpenCilk framework</ListItem>
                    <ListItem>Correctness across all test cases</ListItem>
                    <ListItem>Performance on large-scale graphs</ListItem>
                    <ListItem>Scalable parallel implementations</ListItem>
                  </UnorderedList>
                </VStack>
              </SimpleGrid>
            </VStack>
          </CardBody>
        </Card>

        <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
          <CardHeader>
            <Heading size="md">Graph Datasets</Heading>
          </CardHeader>
          <CardBody pt={0}>
            <VStack align="start" spacing={4}>
              <Text color={textSecondary}>
                The contest featured diverse graph datasets designed to test different aspects of 
                parallel graph algorithm performance:
              </Text>
              
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} width="100%">
                <VStack align="start" spacing={2}>
                  <Text fontWeight="semibold">Real-World Networks</Text>
                  <List spacing={1} fontSize="sm" color={textSecondary}>
                    <ListItem>• Social networks (LiveJournal, Twitter)</ListItem>
                    <ListItem>• Web graphs (Wikipedia)</ListItem>
                    <ListItem>• Collaboration networks (Hollywood)</ListItem>
                  </List>
                </VStack>
                
                <VStack align="start" spacing={2}>
                  <Text fontWeight="semibold">Synthetic Graphs</Text>
                  <List spacing={1} fontSize="sm" color={textSecondary}>
                    <ListItem>• Erdős-Rényi random graphs</ListItem>
                    <ListItem>• Grid-based topologies</ListItem>
                    <ListItem>• Road network simulations</ListItem>
                  </List>
                </VStack>
              </SimpleGrid>
            </VStack>
          </CardBody>
        </Card>

        <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
          <CardHeader>
            <Heading size="md">Evaluation Methodology</Heading>
          </CardHeader>
          <CardBody pt={0}>
            <VStack align="start" spacing={4}>
              <Text color={textSecondary}>
                Submissions were evaluated using a comprehensive methodology that balanced 
                correctness, performance, and scalability:
              </Text>
              
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} width="100%">
                <Card bg={accentBg} p={4}>
                  <VStack align="start" spacing={2}>
                    <Text fontWeight="semibold" color="blue.600">Correctness</Text>
                    <Text fontSize="sm" color={textSecondary}>
                      Solutions must produce correct results across all test cases
                    </Text>
                  </VStack>
                </Card>
                
                <Card bg={accentBg} p={4}>
                  <VStack align="start" spacing={2}>
                    <Text fontWeight="semibold" color="blue.600">Performance</Text>
                    <Text fontSize="sm" color={textSecondary}>
                      Runtime optimization on large-scale graph datasets
                    </Text>
                  </VStack>
                </Card>
                
                <Card bg={accentBg} p={4}>
                  <VStack align="start" spacing={2}>
                    <Text fontWeight="semibold" color="blue.600">Scalability</Text>
                    <Text fontSize="sm" color={textSecondary}>
                      Parallel efficiency and strong scaling properties
                    </Text>
                  </VStack>
                </Card>
              </SimpleGrid>
            </VStack>
          </CardBody>
        </Card>
      </VStack>
    );
  };

  return (
    <Box bg={bgColor} minHeight="100vh">
      <Container maxW="7xl" py={8}>
        <VStack spacing={8} align="stretch">
          {/* Breadcrumb Navigation */}
          <Breadcrumb>
            <BreadcrumbItem>
              <BreadcrumbLink as={RouterLink} to="/contests">
                Contests
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbItem isCurrentPage>
              <BreadcrumbLink href="#">{contest.shortName}</BreadcrumbLink>
            </BreadcrumbItem>
          </Breadcrumb>

          {/* Contest Header */}
          <Card bg={cardBg} borderColor={borderColor} borderWidth="1px" overflow="hidden">
            <Box
              height="120px"
              bgGradient={`linear(to-r, ${contest.theme.primaryColor}, ${contest.theme.secondaryColor})`}
              position="relative"
            >
              <Box
                position="absolute"
                top={4}
                right={4}
              >
                <Badge colorScheme={statusColor} variant="solid" fontSize="sm" px={3} py={1}>
                  {statusDisplay}
                </Badge>
              </Box>
            </Box>
            
            <CardBody pt={6}>
              <VStack align="start" spacing={4}>
                <VStack align="start" spacing={2}>
                  <Heading size="xl" color={contest.theme.primaryColor}>
                    {contest.name}
                  </Heading>
                  {contest.venue && (
                    <HStack spacing={2}>
                      <Icon as={CalendarIcon} color={textSecondary} />
                      <Text color={textSecondary} fontWeight="medium">
                        {contest.venue}
                      </Text>
                    </HStack>
                  )}
                </VStack>
                
                <Text fontSize="lg" color={textSecondary}>
                  {contest.description}
                </Text>

                {/* Contest Stats */}
                <SimpleGrid columns={{ base: 2, md: 4 }} spacing={6} width="100%">
                  <Stat>
                    <StatLabel>Problems</StatLabel>
                    <StatNumber>{contest.problems.length}</StatNumber>
                    <StatHelpText>Algorithm Challenges</StatHelpText>
                  </Stat>
                  
                  {contest.startDate && (
                    <Stat>
                      <StatLabel>Start Date</StatLabel>
                      <StatNumber fontSize="md">
                        {contest.startDate.toLocaleDateString()}
                      </StatNumber>
                      <StatHelpText>Contest Period</StatHelpText>
                    </Stat>
                  )}
                  
                  {contest.endDate && (
                    <Stat>
                      <StatLabel>End Date</StatLabel>
                      <StatNumber fontSize="md">
                        {contest.endDate.toLocaleDateString()}
                      </StatNumber>
                      <StatHelpText>Contest Period</StatHelpText>
                    </Stat>
                  )}
                  
                  <Stat>
                    <StatLabel>Status</StatLabel>
                    <StatNumber fontSize="md">{statusDisplay}</StatNumber>
                    <StatHelpText>Competition State</StatHelpText>
                  </Stat>
                </SimpleGrid>

                {/* Action Buttons */}
                <HStack spacing={4} pt={4} flexWrap="wrap">
                  {contest.leaderboardUrl && (
                    <Button
                      as={RouterLink}
                      to={contest.leaderboardUrl}
                      colorScheme="blue"
                      leftIcon={<StarIcon />}
                    >
                      View Leaderboard
                    </Button>
                  )}
                  
                  {contest.problems.length > 0 && (
                    <Button
                      as={RouterLink}
                      to={`/problem/${contest.problems[0]}`}
                      variant="outline"
                      colorScheme="blue"
                      leftIcon={<ViewIcon />}
                    >
                      Start with {contest.problems[0] ? getContestProblemShortName(contest.problems[0]) : 'BFS'}
                    </Button>
                  )}
                  
                  {contest.problems.length > 1 && (
                    <Button
                      as={RouterLink}
                      to={`/problem/${contest.problems[1]}`}
                      variant="outline"
                      leftIcon={<ViewIcon />}
                    >
                      Start with {contest.problems[1] ? getContestProblemShortName(contest.problems[1]) : 'SSSP'}
                    </Button>
                  )}
                </HStack>
              </VStack>
            </CardBody>
          </Card>

          {/* Contest Problems */}
          <VStack align="start" spacing={6}>
            <Heading size="lg">Contest Problems</Heading>
            
            {contest.problems.length > 0 ? (
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} width="100%">
                {contest.problems.map((problemId, index) => (
                  <ProblemCard key={problemId} problemId={problemId} index={index} />
                ))}
              </SimpleGrid>
            ) : (
              <Alert status="info" borderRadius="md">
                <AlertIcon />
                No problems are currently available for this contest.
              </Alert>
            )}
          </VStack>

          {/* Contest-Specific Information */}
          {getSPE4ICContent()}

          {/* General Contest Information */}
          <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
            <CardHeader>
              <Heading size="md">Technical Details</Heading>
            </CardHeader>
            <CardBody pt={0}>
              <VStack align="start" spacing={4}>
                <Text color={textSecondary}>
                  This contest challenges participants to develop high-performance parallel algorithms 
                  using modern programming techniques and optimization strategies.
                </Text>
                
                <Divider />
                
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} width="100%">
                  <VStack align="start" spacing={2}>
                    <Text fontWeight="semibold">Programming Environment</Text>
                    <List spacing={1} fontSize="sm" color={textSecondary}>
                      <ListItem>• C++ programming language</ListItem>
                      <ListItem>• OpenCilk parallel programming framework</ListItem>
                      <ListItem>• Performance profiling and analysis tools</ListItem>
                      <ListItem>• Automated testing and benchmarking</ListItem>
                    </List>
                  </VStack>
                  
                  <VStack align="start" spacing={2}>
                    <Text fontWeight="semibold">Optimization Focus</Text>
                    <List spacing={1} fontSize="sm" color={textSecondary}>
                      <ListItem>• Parallel algorithm design</ListItem>
                      <ListItem>• Memory access optimization</ListItem>
                      <ListItem>• Load balancing strategies</ListItem>
                      <ListItem>• Cache-efficient data structures</ListItem>
                    </List>
                  </VStack>
                </SimpleGrid>
              </VStack>
            </CardBody>
          </Card>
        </VStack>
      </Container>
    </Box>
  );
};

export default ContestDetail; 